import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, Validators, FormArray, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BackboneComponent } from '@components/backbone/backbone.component';
import { BreadcrumbComponent } from '@components/breadcrumb/breadcrumb.component';
import { environment } from '@environments/environment';
import { RegistroCivilService } from '@services/registro-civil.service';
import { requiredFileType, requiredFileSize } from '@utils/validators';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroup } from 'primeng/inputgroup';
import { TooltipModule } from 'primeng/tooltip';
import { ToastService } from '@utils/toast.service';

@Component({
  selector: 'page-marco-folio-reverso',
  imports: [
    CommonModule,
    BackboneComponent,
    BreadcrumbComponent,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    RouterModule,
    InputSwitchModule,
    DropdownModule,
    CalendarModule,
    ToggleSwitchModule,
    IconFieldModule,
    InputIconModule,
    InputGroupAddonModule,
    InputGroup,
    TooltipModule,
  ],
  templateUrl: './marco-folio-reverso.component.html',
  styleUrl: './marco-folio-reverso.component.scss',
})
export class MarcoFolioReversoPage {
  private formBuilder = inject(FormBuilder);
  private registroCivilService = inject(RegistroCivilService);
  private toastService = inject(ToastService);

  public apiUrl = environment.app.apiUrl;
  public maxSizesUpload = 3000000;
  public isUploading = false;

  public list: any = [];
  public rangeDates: Date[] | undefined;
  public searchValue: string | undefined;

  public formUpload = this.formBuilder.group({
    files: this.formBuilder.array([]),
    marcosChecked: [true, Validators.required],
    foliosChecked: [false, Validators.required],
    reversosChecked: [false, Validators.required],
    test: [null, Validators.required],
  });

  get marcosChecked() {
    return this.formUpload.get('marcosChecked');
  }
  set marcosChecked(value: any) {
    this.marcosChecked.setValue(value);
  }
  get foliosChecked() {
    return this.formUpload.get('foliosChecked');
  }
  set foliosChecked(value: any) {
    this.foliosChecked.setValue(value);
  }
  get reversosChecked() {
    return this.formUpload.get('reversosChecked');
  }
  set reversosChecked(value: any) {
    this.reversosChecked.setValue(value);
  }
  get files(): FormArray {
    return this.formUpload.controls['files'] as FormArray;
  }
  getFilesAsIndex = (index: number, get = 'file') => this.files.controls[index].get(get);

  async ngOnInit() {
    await this.refresh();
  }

  refresh = async () => {
    await this.registroCivilService.list().then((response) => (this.list = response));
  };

  onInputFileChanged = (event: any) => {
    if (event.target.files && event.target.files.length) {
      for (const element of event.target.files) {
        let reader = new FileReader();

        reader.onload = (e: any) => {
          this.files.push(
            this.formBuilder.group({
              file: [element, [requiredFileType(['pdf']), requiredFileSize(this.maxSizesUpload)]],
              url: e.target.result, // Base64 string for preview image,
              upload: false,
            })
          );
        };

        reader.readAsDataURL(element);
      }
    }

    event.target.value = null;
  };

  removeFile = (index: number) => this.files.removeAt(index);

  upload = async () => {
    this.isUploading = true;

    const myFormData = new FormData();

    myFormData.append('marcosChecked', this.marcosChecked.value);
    myFormData.append('foliosChecked', this.foliosChecked.value);
    myFormData.append('reversosChecked', this.reversosChecked.value);

    for (const [index, item] of this.files.value.entries()) {
      if (this.getFilesAsIndex(index)?.errors === null) {
        myFormData.append('files[]', item.file, item.file.name);
      }
    }

    this.registroCivilService
      .upload(myFormData)
      .then(() => {
        this.isUploading = false;
        this.files.clear();
        this.marcosChecked = true;
        this.foliosChecked = false;
        this.reversosChecked = false;

        this.refresh();
      })
      .catch((err) => {
        this.isUploading = false;
        this.toastService.error(err.error.data.message || 'Error al subir los archivos.');
      });
  };
}
